import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  FileText,
  CheckCircle2,
  XCircle,
  Edit3,
  Shield,
  Server,
  AlertTriangle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  'Business Continuity': FileText,
  'Disaster Recovery': Server,
  'Security': Shield,
};

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (templateId, currentStatus) => {
    try {
      if (currentStatus) {
        await axios.delete(`${API_URL}/admin/templates/${templateId}`);
        toast.success('Template deactivated');
      } else {
        await axios.put(`${API_URL}/admin/templates/${templateId}`, { is_active: true });
        toast.success('Template activated');
      }
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <PageHeader
          title="Template Management"
          description="View and manage compliance document templates."
        />

        {/* Info Banner */}
        <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#FF6B35] mt-0.5" />
            <div>
              <h3 className="font-medium text-slate-900">Template Management</h3>
              <p className="text-sm text-slate-600 mt-1">
                Templates are pre-configured for industry compliance standards. 
                You can activate or deactivate templates to control which ones are available to clients.
              </p>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Loading templates...</p>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {templates.map((template) => {
              const Icon = categoryIcons[template.category] || FileText;
              return (
                <div
                  key={template.id}
                  className={`bg-white rounded-xl border p-6 transition-all duration-200 ${
                    template.is_active 
                      ? 'border-slate-200 hover:shadow-lg' 
                      : 'border-slate-200 bg-slate-50 opacity-75'
                  }`}
                  data-testid={`template-card-${template.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        template.is_active ? 'bg-[#FF6B35]/10' : 'bg-slate-200'
                      }`}>
                        <Icon className={`h-6 w-6 ${template.is_active ? 'text-[#FF6B35]' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {template.name}
                        </h3>
                        <span className="text-xs text-slate-500">{template.category}</span>
                      </div>
                    </div>
                    
                    {template.is_active ? (
                      <span className="status-success flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Active
                      </span>
                    ) : (
                      <span className="status-neutral flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        Inactive
                      </span>
                    )}
                  </div>

                  <p className="text-slate-600 text-sm mb-4">
                    {template.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="text-sm text-slate-500">
                      {template.sections?.length || 0} sections
                    </div>
                    
                    <Button
                      variant={template.is_active ? "outline" : "default"}
                      size="sm"
                      onClick={() => handleToggleActive(template.id, template.is_active)}
                      className={!template.is_active ? 'btn-primary' : ''}
                      data-testid={`toggle-template-${template.id}`}
                    >
                      {template.is_active ? (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Activate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
